import React, { useEffect, useState, useRef, useMemo } from 'react';
import { useLocation, useParams, useNavigate } from 'react-router-dom';
import VideoJS from '../../Components/Videojs/VideoJS';
import Loader from '../../Components/Loader/Loader';
import Header from '../../Components/Header/Header';
import ConfirmationBox from '../../Components/Confirmation/ConfirmationBox';
import { useAuthContext } from '../../hooks/UseAuthContext';
import api from '../../api';
import { toast } from 'react-toastify';
import GetChannel from '../Channels/GetChannel';
import './completedvids.css';

function CompletedVideo() {
  const playerRef = useRef(null);
  const { id } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const { user } = useAuthContext();

  const [videoData, setVideoData] = useState(null);
  const [script, setScript] = useState('');
  const [loading, setLoading] = useState(false);
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [error, setError] = useState(null);
  const [chooseChannel, setChooseChannel] = useState(false);
  const [channelData, setChannelData] = useState(null);

  // Update videoData when channel is selected
  useEffect(() => {
    if (channelData) {
      setVideoData(prev => ({
        ...prev,
        channelId: channelData._id,
        avatarImage: channelData.backgroundImage,
        backgroundVideo: channelData.backgroundVideo,
        channelLogo: channelData.logo,
        voice: channelData.voice,
        backgroundMusic: channelData.background_music,
      }));
    }
  }, [channelData]);

  // Initialize from location.state or fetch from API
  useEffect(() => {
    const stateData = location.state?.videoUrl;

    if (stateData) {
      setVideoData(stateData);
      setScript(
        `${stateData.sections?.intro?.section_script || ''}\n\n${stateData.sections?.body?.section_script || ''}`
      );
    } else {
      const fetchVideo = async () => {
        setLoading(true);
        try {
          const response = await api.get(`/video/${id}`, {
            headers: { Authorization: `Bearer ${user.token}` },
          });

          setVideoData(response.data);
          setScript(
            `${response.data.sections?.intro?.section_script || ''}\n\n${response.data.sections?.body?.section_script || ''}`
          );
        } catch (err) {
          console.error('Error fetching video data:', err);
          setError('Failed to load video.');
        } finally {
          setLoading(false);
        }
      };

      if (id && user?.token) {
        fetchVideo();
      }
    }
  }, [location.state, id, user?.token]);

  // Video.js options
  const videoJsOptions = useMemo(() => {
    if (!videoData?.video_path) return null;

    return {
      controls: true,
      responsive: true,
      fluid: true,
      sources: [
        {
          viewSrc: `${process.env.REACT_APP_API_URL_VIDS || 'https://api.alchemymix.com'}/videos/${videoData.video_path}`,
          src:`${process.env.REACT_APP_API_URL_VIDS}/videos/${videoData.video_path}`,
          type: 'video/mp4',
        },
      ],
    };
  }, [videoData]);

  const handlePlayerReady = (player) => {
    playerRef.current = player;
  };

  const downloadVideo = (videoUrl, videoName) => {
    const link = document.createElement('a');
    link.href = videoUrl;
    link.setAttribute('download', videoName || 'video.mp4');
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  // ── Send video to Timeline editor ─────────────────────────────
  const handleEditInTimeline = () => {
    if (!videoData?.video_path) return;
    navigate('/timeline', {
      state: {
        timelineVideo: {
          url: `${process.env.REACT_APP_API_URL_VIDS}/videos/${videoData.video_path}`,
          filename: videoData.video_path.split('/').pop() || 'video.mp4',
        },
      },
    });
  };

  const handleRegenerate = async () => {
    setLoading(true);
    setError(null);

    try {
      const response = await api.post(
        '/regenerate-video',
        {
          asset_folder: videoData.asset_folder,
          data: videoData,
        },
        {
          headers: {
            Authorization: `Bearer ${user.token}`,
            'Content-Type': 'application/json',
          },
        }
      );

      if (response.status === 200 || response.status === 202) {
        toast.success(response.data?.msg || 'Video regeneration in progress!');
      }
    } catch (err) {
      console.error('Regeneration failed:', err);
      setError('Failed to regenerate video. Please try again.');
      toast.error('Failed to regenerate video');
    } finally {
      setLoading(false);
    }
  };

  const handleConfirm = (confirmed) => {
    setShowConfirmation(false);
    if (confirmed) handleRegenerate();
  };

  if (loading && !videoData) return <Loader />;
  if (error && !videoData) return (
    <div className="overall-complete-vid">
      <Header />
      <div className="error-message" style={{ margin: '40px auto', maxWidth: '600px' }}>
        {error}
      </div>
    </div>
  );
  if (!videoData) return null;

  return (
    <div className="overall-complete-vid">
      <Header />

      <div className="single-completed-vid">
        {/* Video Player */}
        <div className="video-result">
          {videoJsOptions && (
            <VideoJS options={videoJsOptions} onReady={handlePlayerReady} />
          )}

          <div className="single-vid-btn">
            {/* Download */}
            {videoJsOptions?.sources?.[0]?.viewSrc && (
              <button
                className="download-btn"
                onClick={() =>
                  downloadVideo(
                    videoJsOptions.sources[0].viewSrc,
                    videoData?.video_path.split('/').pop()
                  )
                }
              >
                ⬇ Download
              </button>
            )}

            {/* ── Edit in Timeline ── */}
            <button
              className="timeline-btn"
              onClick={handleEditInTimeline}
              disabled={!videoData?.video_path}
              title="Open in Timeline Editor"
            >
              ✂ Edit in Timeline
            </button>

            {/* Regenerate */}
            <button
              className="regenerate-btn"
              onClick={() => setChooseChannel(true)}
              disabled={loading}
            >
              {loading ? 'Regenerating…' : '🔄 Regenerate'}
            </button>
          </div>

          {error && <p className="error-message">{error}</p>}
        </div>

        {/* Script */}
        <div className="video-script">
          <textarea
            name="text-intro"
            id="text-intro"
            value={script}
            readOnly
            placeholder="Script content will appear here…"
          />
        </div>
      </div>

      {/* Channel Selection Modal */}
      {chooseChannel && (
        <div className="composition-channel">
          <div className="get-channel">
            <GetChannel setChannelData={setChannelData} />
            <div className="composition-channel-btns">
              <button
                onClick={() => {
                  setChooseChannel(false);
                  setShowConfirmation(true);
                }}
              >
                Proceed
              </button>
              <button onClick={() => setChooseChannel(false)}>
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Confirmation Dialog */}
      {showConfirmation && (
        <ConfirmationBox
          message="Are you sure you want to regenerate this video?"
          handleConfirm={handleConfirm}
        />
      )}

      {/* Loading Overlay */}
      {loading && videoData && <Loader />}
    </div>
  );
}

export default CompletedVideo;